import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/legacy/x-data-view.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { View } from '@adornis/data-view/db/view.js';
import { shouldIncludeFieldHelpers } from '@adornis/field-management/field-management-options.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { DateTimeAspectSchema } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { FilterEditor } from '@adornis/filter/client/filter-editor.js';
import type { FilterEditorOptions } from '@adornis/filter/filter-options.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { XViewManager } from '@adornis/view-manager/client/x-view-manager.js';
import { computed } from '@lit-labs/preact-signals';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { PageHeader } from '@martinus/styles/client/PageHeader.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { live } from 'lit/directives/live.js';
import { repeat } from 'lit/directives/repeat.js';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { buildLegacyDebitorNumberLabels } from '../../../debitor-numbers/legacyDebitorNumberLabels.js';
import { getAllSchools } from '../../../groups/api/getAllSchools.js';
import { Group } from '../../../groups/entities/Group.js';
import { getSchoolIDsForRoles } from '../../../permissions/api/getSchoolIDsForRoles.js';
import { NoPermission } from '../../../permissions/client/NoPermission.js';
import { useMartinusABAC } from '../../../permissions/client/useMartinusAbac.js';
import { Role } from '../../../permissions/roles.js';
import {
  subscribeUserBasedViewProfiles,
  UserBasedViewProfile,
} from '../../../view-manager/subscribeUserBasedViewProfiles.js';
import { openShareViewProfileDialog } from '../../../view-manager/client/ShareViewProfileDialog.js';
import { Person } from '../../entities/person.js';
import { UserActions } from '../../UserPolicy.js';
import { UserListAPI } from '../api/UserListAPI.js';
import type { UserListSelectionInterface } from '../entities/UserListSelectionInterface.js';
import { UserListActions } from '../UserListPolicy.js';
import { UserListCustomFilterFields } from './UserListCustomFilterFields.js';
import { UserListInlineControls } from './UserListInlineControls.js';
import { UserListOperations } from './UserListOperations.js';
import {
  fullDataviewWhitelist,
  fullDataviewWhitelistFilter,
  simpleDataviewWhitelist,
} from './UserListSelectionsets.js';
import { enrichFilterWithDefaultFilter, UserListStandardView } from './UserListStandardView.js';
import { useUserListPreselectedFilter } from './useUserListPreselectedFilter.js';
import { useUserSelection } from './useUserSelection.js';
import { TailwindScope } from '../../../client/components/TailwindScope.js';

const category = 'users-list';

function hideSnackbar() {
  const snackbarElement = document.querySelector<SgSnackbar>('sg-snackbar');
  if (snackbarElement) {
    void snackbarElement.hide();
  }
}

let timeout: NodeJS.Timeout | null = null;

/**
 * Dispatches a save operation for the view. Debounces the save operation to prevent too many save operations if the user updates the view in quick succession like typing in filters.
 * @param view the view to be saved. Must have an _id already
 */
function dispatchViewSave(view: View<typeof Person>, usesPrefilledFilter: boolean) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (usesPrefilledFilter) return;
  localStorage.setItem(`${category + (view._id ?? 'default')}-filter`, JSON.stringify(view.filter));

  if (!view._id) return;

  const saveFunction = () => {
    view
      .save()
      .then(() => SgSnackbar.show('Ansicht gespeichert', 3000))
      .catch(async error => {
        /**
         * Remove zombie views from localStorage if save fails
         */
        localStorage.removeItem(`${category + (view._id ?? 'default')}-filter`);
        void SgSnackbar.show(error.message);
      });
  };

  const debounce = (() => {
    return (fn: () => void) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        fn();
        timeout = null;
      }, 1000);
    };
  })();

  debounce(() =>
    SgSnackbar.show(
      () => html`
        <div class="flex flex-row items-center justify-between gap-24">
          <span>Ansicht geändert.</span>
          <button type="button" class="btn btn-primary" @click=${saveFunction}>
            <span class="icon-[material-symbols--save-rounded] size-5"></span>
            Änderungen speichern
          </button>
        </div>
      `,
      600000,
    ),
  );
}

const ResolveSchoolsByIDs = stateful((props: { schoolIDs: Maybe<string[]> }) => {
  const { value, loading } = useQuery(
    () =>
      getByID(
        Group,
        props.schoolIDs,
      )({
        _id: 1,
        name: 1,
      }),
    [props.schoolIDs],
  );
  if (loading) return '⌛';
  if (!value?.length) return nothing;

  return html`<p class="truncate">• ${value.map(school => school.name).join(', ')}</p>`;
});

export const UserList = stateful(
  (props?: {
    userSelection?: {
      actionName: string;
      resolveUserSelection: (selection: Maybe<UserListSelectionInterface>) => void;
      rejectUserSelection: () => void;
    };
  }) => {
    const user = useObservable(() => currentUser(), []);

    const { can: canSeeList, reason } = useMartinusABAC({
      action: UserListActions.USER_LIST,
    });

    const { can: canUseFullFeatures } = useMartinusABAC({
      action: UserListActions.USER_LIST_FULL_FEATURES,
    });

    const { can: canImpersonate } = useMartinusABAC({
      action: UserActions.IMPERSONATE,
    });

    const { can: canRemoveUser } = useMartinusABAC({
      action: UserActions.REMOVE_USER,
    });

    if (!canSeeList) {
      return NoPermission({
        message: reason,
      });
    }

    /**
     * Users can often see other users of their school. But if the other user is at multiple school the user might see the role the other user has in another school.
     * To prevent this we only show the roles that are at the same schools as the current user.
     */
    const [schoolsOfUser, setSchoolsOfUser] = useState<string[]>([]);
    useEffect(() => {
      void getSchoolIDsForRoles(user, [Role.PSB, Role.SCHULLEITER, Role.SEKRETAER, Role.SPENDEN_ADMIN]).then(schools =>
        setSchoolsOfUser(schools),
      );
    }, [user]);

    // the scope of a legacy debitor number is a school, so we resolve it against the school cache to show its kuerzel
    const { value: schools } = useQuery(() => getAllSchools(), []);

    const [showFullPath, setShowFullPath] = useState(false);
    const [view, setView] = useState<View<typeof Person> | null>(null);

    const { preSelectedFilter, preSelectedFilterPersonCount, setManualSearch, manualSearch } =
      useUserListPreselectedFilter(view, setView);

    const {
      selectedAll,
      hasCustomSelection,
      isSelected,
      toggleSelection,
      resetSelection,
      getSelectionInterface,
      toggleSelectAll,
    } = useUserSelection();

    const actionsAvailable = [canImpersonate, canRemoveUser].filter(Boolean);

    const { value: dataViewOptions } = computed(() => {
      const dv = {
        class: Person,
        // @ts-expect-error dataview cannot use narrowed entity at the moment it is only typed for complete entities
        operation: UserListAPI.subscribe,
        isColumnSelectable: canUseFullFeatures,
        isColumnResortable: canUseFullFeatures,
        isColumnResizable: canUseFullFeatures,
        showFullFieldPath: canUseFullFeatures && showFullPath,
        view: view ?? new View({}),
        defaultFieldMapping: {
          number: v => v.toLocaleString('de-DE'),
          Date: v => formatCalendarDay(v, 'dd.MM.yyyy'),
          Datetime: v => format(v, 'dd.MM.yyyy HH:mm'),
        },
        customColumns: [
          ...(actionsAvailable.length
            ? [
                {
                  customID: '_actions',
                  columnName: '',
                  position: 'back' as const,
                  disabledHref: true,
                  columnWidth: '118px',
                  isAlwaysVisible: true,
                  isSortable: false,
                  isResizable: false,
                  isSticky: true,
                  renderItem: (
                    user: NarrowedEntity<
                      Person,
                      { email: 1; _id: 1; profilDaten: { stammdaten: { vorname: 1; nachname: 1 } } }
                    >,
                  ) =>
                    UserListInlineControls.render({
                      user,
                    }),
                } satisfies ColumnDefinition<typeof Person>,
              ]
            : []),
          {
            customID: '_selection',
            columnName: '',
            // legacy x-data-view cells live in a shadow root without Tailwind, see TailwindScope
            renderHeader: () =>
              TailwindScope.render({
                content: html`
                  <!-- the header cell is a start-aligned flex row and the scope shrinks to its content, so the wrapper takes
                       the column's width (columnWidth below) to centre the button over the checkboxes -->
                  <div class="flex w-[38px] items-center justify-center">
                    <button
                      type="button"
                      class=${
                        selectedAll && !hasCustomSelection()
                          ? 'btn btn-square btn-ghost btn-sm text-success hover:text-error'
                          : selectedAll
                            ? 'btn btn-square btn-ghost btn-sm text-base-content/70 hover:text-error'
                            : 'btn btn-square btn-ghost btn-sm text-base-content/70 hover:text-success'
                      }
                      aria-label=${selectedAll ? 'Auswahl aufheben' : 'Alle auswählen'}
                      @click=${toggleSelectAll}
                    >
                      ${
                        selectedAll
                          ? html`<span class="icon-[material-symbols--deselect-rounded] size-5"></span>`
                          : html`<span class="icon-[material-symbols--select-all-rounded] size-5"></span>`
                      }
                    </button>
                  </div>
                `,
              }),
            /**
             * A bare row selector, not the `CheckboxInput` form field: the field's label row is wider than this 38px
             * column and pins the box to its top, so it overflowed under the column divider. `live()` re-syncs the box
             * with the real selection, which `toggleSelection` may refuse (the 40-ids cap), and the click stops here so
             * no row-level handler of the data-view sees it.
             */
            renderItem: (person: { _id: string }) =>
              TailwindScope.render({
                content: html`<div class="flex items-center justify-center">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-sm checkbox-primary"
                    aria-label="Person auswählen"
                    .checked=${live(isSelected(person._id))}
                    @click=${(event: Event) => event.stopPropagation()}
                    @change=${() => toggleSelection(person._id)}
                  />
                </div>`,
              }),
            disabledHref: true,
            columnWidth: '38px',
            position: 'front',
            isAlwaysVisible: true,
            isSortable: false,
            isResizable: false,
            isSticky: true,
          },

          {
            fieldName: 'roles.name',
            renderItem: (person: { roles: { name: string; contexts: string[] }[] }) =>
              TailwindScope.render({
                content: html`
                  <div class="grid grid-cols-1 gap-2">
                    ${repeat(
                      person.roles
                        .filter(role =>
                          role.contexts.some(context =>
                            schoolsOfUser.length ? schoolsOfUser.includes(context) : true,
                          ),
                        )
                        .map(role => role.name)
                        .filter(name => name !== Role.ADMIN && name !== 'ChangePassword'),
                      name => name,
                      name => html`<p class="truncate">• ${name}</p>`,
                    )}
                  </div>
                `,
              }),
          },
          {
            fieldName: 'debitorennummer',
            // a custom selectionSet replaces the one generated from the fieldName, so the debitorennummer has to be part of it
            selectionSet: { debitorennummer: 1, legacyDebitorNumbers: { debitorNumber: 1, scope: 1 } },
            renderItem: (person: {
              debitorennummer: Maybe<string>;
              legacyDebitorNumbers: Maybe<{ scope: Maybe<string>; debitorNumber: Maybe<string> }[]>;
            }) =>
              TailwindScope.render({
                content: html`
                  <div class="flex flex-col min-w-0">
                    <span class="truncate">${person.debitorennummer}</span>
                    ${repeat(
                      buildLegacyDebitorNumberLabels(person.legacyDebitorNumbers, schools),
                      label => label,
                      label => html`
                        <span class="truncate badge badge-sm badge-outline text-base-content/70"> ${label} </span>
                      `,
                    )}
                  </div>
                `,
              }),
          },
          {
            fieldName: 'roles.contexts',
            renderItem: (person: { roles: { name: string; contexts: string[] }[] }) =>
              TailwindScope.render({
                content: html`
                  <div class="grid grid-cols-1 gap-2">
                    ${repeat(
                      person.roles.filter(role => role.name !== Role.ADMIN && role.name !== 'ChangePassword'),
                      role => role.contexts.join(', '),
                      role =>
                        ResolveSchoolsByIDs({
                          schoolIDs: role.contexts.filter(context =>
                            schoolsOfUser.length ? schoolsOfUser.includes(context) : true,
                          ),
                        }),
                    )}
                  </div>
                `,
              }),
          },
        ],
        fieldSelectionOptions: {
          shouldIncludeField: shouldIncludeFieldHelpers.bySelectionSet({
            whitelist: canUseFullFeatures ? fullDataviewWhitelist : simpleDataviewWhitelist,
          }),
        },

        onViewChanged: view => {
          setView(view);
          dispatchViewSave(view, !!preSelectedFilter);
        },
        rowHref: entity => `/personen/${entity._id}`,
      } satisfies DataViewOptions<typeof Person>;
      return dv;
    });

    const { value: filterOptions } = computed(
      () =>
        ({
          filter: view?.filter ?? new AdornisFilter({}),
          searchable: true,
          fieldAspectGeneration: preSelectedFilter
            ? undefined
            : {
                class: Person,
                shouldIncludeField: shouldIncludeFieldHelpers.bySelectionSet({
                  whitelist: canUseFullFeatures ? fullDataviewWhitelistFilter : simpleDataviewWhitelist,
                }),
              },

          overridesForIdentifier: {
            contractApplyDate: DateTimeAspectSchema('is'),
          },

          overridesForFieldName: UserListCustomFilterFields,
          onFilterChanged: filter => {
            if (!view) return;
            view.filter = filter;
            setView(view);

            // if the filter changed we should clear the selection as the user might have ids selected which are not visible anymore
            resetSelection();

            dispatchViewSave(view, !!preSelectedFilter);
          },
        }) satisfies FilterEditorOptions<typeof Person>,
    );

    const options = buildFullOptions(dataViewOptions, filterOptions);

    const ListOperations = !props?.userSelection
      ? html`
          <div class="flex flex-row gap-2">
            ${UserListOperations({
              dataviewConfig: options.dataViewOptions,
              selectedInterface: getSelectionInterface(view?.filter),
              setShowFullPath,
            })}
          </div>
        `
      : html`
          <button
            class="btn btn-success"
            @click=${() => props.userSelection?.resolveUserSelection(getSelectionInterface(view?.filter))}
            ?disabled=${!getSelectionInterface(view?.filter)}
          >
            ${props.userSelection.actionName}
          </button>
        `;

    return html`
      ${
        props?.userSelection
          ? DialogHeader({
              title: props.userSelection.actionName,
              close: props.userSelection.rejectUserSelection,
              closeLabel: 'Dialog schließen',
            })
          : PageHeader({
              title: `Personen${preSelectedFilter ? ` vorgefiltert: ${preSelectedFilterPersonCount}` : ''}`,
            })
      }

      <div class="card flex flex-col grow m-4 gap-4">
        <div class="flex p-4 justify-between">
          ${
            preSelectedFilter
              ? html`
                  <div class="flex gap-2 grow">
                    ${TextInput({
                      placeholder: 'Suche...',
                      value: manualSearch,
                      onChanged: value => {
                        setManualSearch(value ?? '');
                      },
                      clearable: true,
                      suffix: html`<span
                        class="icon-[material-symbols--search] text-base text-base-content/70"
                      ></span>`,
                    })}
                    ${ListOperations}
                  </div>
                `
              : FilterEditor({
                  noCollapse: true,
                  fullWidth: true,
                  options: options.filterOptions,
                  onFilterReset: () => {
                    if (!view?.filter) return;
                    view.filter.search = '';
                    enrichFilterWithDefaultFilter(view);
                  },
                  additionalButtons: () => html`
                    <div slot="bigFilterEnd" class="flex gap-4 items-start">
                      ${
                        preSelectedFilter
                          ? nothing
                          : XViewManager<typeof UserBasedViewProfile>({
                              category,
                              operations: {
                                transformViewBeforeCreate: viewProfile => {
                                  const newViewProfile = new UserBasedViewProfile(viewProfile);

                                  newViewProfile.userId = user?._id;
                                  return newViewProfile;
                                },
                                subscribeViews: subscribeUserBasedViewProfiles,
                              },
                              defaultView: UserListStandardView,
                              viewChanged: (nextView, manualFire) => {
                                hideSnackbar();

                                // load the filter from local storage if the user initally loads the view or we are loading the same view again (coming from another page)
                                // This way the user will never loose his filter settings. If he comes back to the view from another page
                                // if he switches views we will instead use the given filter of the view as it then comes from the database or from the default page
                                if (
                                  (!view || (!nextView._id && !view._id) || view._id === nextView._id) &&
                                  !manualFire
                                ) {
                                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                                  const filterInLocalStorage = localStorage.getItem(
                                    `${category + (nextView._id ?? 'default')}-filter`,
                                  );
                                  if (filterInLocalStorage) {
                                    nextView.filter = JSON.parse(filterInLocalStorage);
                                  }

                                  // if the view does not have an id we have the default view. We make sure to enrich it with all necessary fields
                                  if (!nextView._id) {
                                    enrichFilterWithDefaultFilter(nextView as View<typeof Person>);
                                  }
                                } else {
                                  // if we are not actively using the filter we reset it in local storage
                                  nextView.revert();
                                  localStorage.removeItem(`${category + (nextView._id ?? 'default')}-filter`);
                                }
                                setView(nextView as View<typeof Person>);
                              },
                            })
                      }
                      ${
                        /**
                         * Only a saved view can be passed on - the standard view exists in this browser only. Whether it
                         * is the user's own is checked on the server: the active view is loaded without its owner.
                         */
                        preSelectedFilter
                          ? nothing
                          : html`<button
                              class="btn btn-soft btn-primary btn-square"
                              title=${view?._id ? 'Ansicht weitergeben' : 'Speichern Sie die Ansicht zuerst, um sie weiterzugeben'}
                              aria-label="Ansicht weitergeben"
                              ?disabled=${!view?._id}
                              @click=${() => view?._id && openShareViewProfileDialog(view._id)}
                            >
                              <span class="icon-[material-symbols--share] text-xl" aria-hidden="true"></span>
                            </button>`
                      }
                      ${ListOperations}
                    </div>
                  `,
                })
          }
        </div>
      </div>

      <div class="overflow-auto bg-white w-full h-full">
        ${
          // wait for the view manager (or the preselected filter) to set the view: the placeholder `new View({})` gets a
          // fresh filter _id on every render, and each one would start and drop a list subscription on the server
          view ? html`<x-data-view .options=${options.dataViewOptions}></x-data-view>` : nothing
        }
      </div>
    `;
  },
);
